/* eslint-disable no-invalid-this */

import { CommandInterfaceEvents, FrameBand, MessageType, NetworkType } from "../emulators";

type FrameConsumer = (rgb: Uint8Array | null, rgba: Uint8Array | null, bands?: FrameBand[]) => void;

export class CommandInterfaceEventsImpl implements CommandInterfaceEvents {
    private onStdoutConsumers: ((message: string) => void)[] = [];
    private delayedStdout: string[] = [];

    private onFrameSizeConsumers: ((width: number, height: number) => void)[] = [];
    private onFrameConsumers: FrameConsumer[] = [];
    private onSoundPushConsumers: ((samples: Float32Array) => void)[] = [];
    private onExitConsumers: (() => void)[] = [];

    private onMessageConsumers: ((msgType: MessageType, ...args: any[]) => void)[] = [];
    private delayedMessages: {msgType: MessageType, args: any[]}[] = [];

    private onNetworkConnectedConsumers: ((networkType: NetworkType, address: string) => void)[] = [];
    private onNetworkDisconnectedConsumers: ((networkType: NetworkType) => void)[] = [];

    private onUnloadConsumers: (() => Promise<void>)[] = [];

    // 3dfx voodoo OpenGL backend lifecycle (docs/3dfx/40-runtime-wiring.md §6).
    // Fired from the protocol layer when worker-protocol.cpp emits ws-voodoo-*.
    private onVoodooActiveConsumers: ((width: number, height: number) => void)[] = [];
    private onVoodooInactiveConsumers: (() => void)[] = [];
    private onVoodooResizeConsumers: ((width: number, height: number) => void)[] = [];

    // Phase 7-r6 — voodoo swap-rate sampler.  Fires 1×/s while voodoo
    // is active; consumer drives the FPS overlay component.
    private onPerformanceFpsConsumers: ((fps: number) => void)[] = [];

    onStdout = (consumer: (message: string) => void) => {
        this.onStdoutConsumers.push(consumer);

        if (this.onStdoutConsumers.length === 1) {
            for (const next of this.delayedStdout) {
                this.fireStdout(next);
            }
            this.delayedStdout = [];
        }
    };

    onFrameSize = (consumer: (width: number, height: number) => void) => {
        this.onFrameSizeConsumers.push(consumer);
    };

    onFrame = (consumer: FrameConsumer) => {
        this.onFrameConsumers.push(consumer);
    };

    onSoundPush = (consumer: (samples: Float32Array) => void) => {
        this.onSoundPushConsumers.push(consumer);
    };

    onExit = (consumer: () => void) => {
        this.onExitConsumers.push(consumer);
    };

    onMessage = (consumer: (msgType: MessageType, ...args: any[]) => void) => {
        this.onMessageConsumers.push(consumer);

        if (this.onMessageConsumers.length === 1) {
            for (const next of this.delayedMessages) {
                consumer(next.msgType, ...next.args);
            }
            this.delayedMessages = [];
        }
    };

    onNetworkConnected(consumer: (networkType: NetworkType, address: string) => void) {
        this.onNetworkConnectedConsumers.push(consumer);
    }

    onNetworkDisconnected(consumer: (networkType: NetworkType) => void) {
        this.onNetworkDisconnectedConsumers.push(consumer);
    }

    onUnload = (consumer: () => Promise<void>) => {
        this.onUnloadConsumers.push(consumer);
    };

    onVoodooActive = (consumer: (width: number, height: number) => void) => {
        this.onVoodooActiveConsumers.push(consumer);
    };

    onVoodooInactive = (consumer: () => void) => {
        this.onVoodooInactiveConsumers.push(consumer);
    };

    onVoodooResize = (consumer: (width: number, height: number) => void) => {
        this.onVoodooResizeConsumers.push(consumer);
    };

    onPerformanceFps = (consumer: (fps: number) => void) => {
        this.onPerformanceFpsConsumers.push(consumer);
    };

    fireStdout = (message: string) => {
        if (this.onStdoutConsumers.length === 0) {
            this.delayedStdout.push(message);
            return;
        }

        for (const next of this.onStdoutConsumers) {
            next(message);
        }
    };

    fireFrameSize = (width: number, height: number) => {
        for (const next of this.onFrameSizeConsumers) {
            next(width, height);
        }
    };

    fireFrame = (rgb: Uint8Array | null, rgba: Uint8Array | null, bands?: FrameBand[]) => {
        for (const next of this.onFrameConsumers) {
            next(rgb, rgba, bands);
        }
    };

    fireSoundPush = (samples: Float32Array) => {
        for (const next of this.onSoundPushConsumers) {
            next(samples);
        }
    };

    fireExit = () => {
        for (const next of this.onExitConsumers) {
            next();
        }

        this.onStdoutConsumers = [];
        this.onFrameSizeConsumers = [];
        this.onFrameConsumers = [];
        this.onSoundPushConsumers = [];
        this.onExitConsumers = [];
        this.onMessageConsumers = [];
    };

    fireMessage = (msgType: MessageType, ...args: any[]) => {
        if (this.onMessageConsumers.length === 0) {
            this.delayedMessages.push({ msgType, args });
            return;
        }

        for (const next of this.onMessageConsumers) {
            next(msgType, ...args);
        }
    };

    fireNetworkConnected = (networkType: NetworkType, address: string) => {
        for (const next of this.onNetworkConnectedConsumers) {
            next(networkType, address);
        }
    };

    fireNetworkDisconnected = (networkType: NetworkType) => {
        for (const next of this.onNetworkDisconnectedConsumers) {
            next(networkType);
        }
    };

    fireUnload = async () => {
        const promises = [];
        for (const next of this.onUnloadConsumers) {
            promises.push(next());
        }
        await Promise.all(promises);
    };

    fireVoodooActive = (width: number, height: number) => {
        for (const next of this.onVoodooActiveConsumers) {
            next(width, height);
        }
    };

    fireVoodooInactive = () => {
        for (const next of this.onVoodooInactiveConsumers) {
            next();
        }
    };

    fireVoodooResize = (width: number, height: number) => {
        for (const next of this.onVoodooResizeConsumers) {
            next(width, height);
        }
    };

    firePerformanceFps = (fps: number) => {
        for (const next of this.onPerformanceFpsConsumers) {
            next(fps);
        }
    };
}
